import { Component, Input, ChangeDetectionStrategy, inject } from "@angular/core";
import { IPopup } from "../../../interfaces/i-popup";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { GeoService } from "../../../services/internal-services/geo.service";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../shared/model/google-analytics-constants";
import { Viewerlayer } from "../../../model/viewerlayer";
import { MapService } from "../../../services/internal-services/map.service";
import { GoogleAnalyticsService } from "../../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../../services/internal-services/viewer-services/inject-base-viewer.service";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "phoenix-feature-detail",
  templateUrl: "./feature-detail.component.html",
  styleUrls: ["./feature-detail.component.scss"],
  standalone: false,
})
export class FeatureDetailComponent {
  viewerService = inject(VIEWER_SERVICE);

  private _feature: any;

  private _additionalFeatures: any[];

  private _layer: Viewerlayer = null;

  infoProperty: IPopup;

  get layer(): Viewerlayer {
    return this._layer;
  }

  @Input() set layer(value: Viewerlayer) {
    this._layer = value;
    this.createInfoProperty();
  }

  get feature(): any {
    return this._feature;
  }

  @Input() set feature(value: any) {
    this._feature = value;
    this.createInfoProperty();
  }

  get additionalFeatures(): any[] {
    return this._additionalFeatures;
  }

  @Input() set additionalFeatures(value: any[]) {
    this._additionalFeatures = value;
  }

  constructor(
    public googleAnalyticsService: GoogleAnalyticsService,
    public mapService: MapService,
  ) {}

  createInfoProperty() {
    if (this.layer && this.feature) {
      let geoJSONWGS84;
      switch (this.feature.geometry.type) {
        case ViewerConstants.GEOMETRY_TYPE.Point:
          geoJSONWGS84 = GeoService.pointToGeoJSONWGS84(this.feature.geometry.coordinates, true);
          this.feature.point = undefined;
          break;
        case ViewerConstants.GEOMETRY_TYPE.Polygon:
          if (this.feature.point) {
            geoJSONWGS84 = GeoService.pointToGeoJSONWGS84(this.feature.point, false);
            this.feature.point = undefined;
          } else {
            const centerCoord = GeoService.getCenterOfGeometry(this.feature);
            geoJSONWGS84 = GeoService.pointToGeoJSONWGS84(centerCoord.geometry.coordinates, true);
          }
          break;
        default:
          geoJSONWGS84 = GeoService.pointToGeoJSONWGS84(
            this.viewerService.getLocation(ViewerConstants.PROJECTIONS.WGS84),
            false,
          );
      }

      const coordinates = geoJSONWGS84.geometry.coordinates;
      this.feature.layerName = this.layer.name;
      this.feature.headers = this.layer.customOverlayProperties
        ? this.layer.customOverlayProperties.layerProperties
        : Object.keys(this.feature.properties ?? {}).filter((k) => !k.startsWith("_"));

      const selectedFeatures = [this.feature];

      const infoProperty: IPopup = {
        coordinates,
        epsg: ViewerConstants.PROJECTIONS.WGS84,
        type: "info-popup",
        featuresData: [this.feature].concat(this.additionalFeatures),
        opts: {
          icon: {
            src: "assets/images/small_pin.svg",
            color: this.viewerService.getPinColor(),
          },
        },
        selected: true,
        property: this.feature.propertyInfo,
        damageAssessment: this.feature.damageInfo,
      };
      this.viewerService.setSelectedFeaturesStyle(selectedFeatures);
      this.viewerService.setSelectedFeatures(selectedFeatures);
      this.infoProperty = infoProperty;

      this.googleAnalyticsService.setGoogleAnalyticsEvent(
        GoogleAnalyticsConstantsEvents.MY_DATA_ZOOM_TO_FEATURE,
        GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
        true,
      );
    }
  }

  onUpdatePropertyInfo(propertyInfo) {
    if (propertyInfo) {
      this.feature.propertyInfo = propertyInfo;
      if (this.layer) {
        const expirationDate = new Date();
        expirationDate.setMonth(expirationDate.getMonth() + 1);
        this.feature.propertyInfo.expiration = expirationDate;
        this.viewerService.updateUserLayer(this.layer);
      }
    }
  }

  onUpdateDamageInfo(damageInfo) {
    if (damageInfo) {
      this.feature.damageInfo = damageInfo;
      if (this.layer) {
        const expirationDate = new Date();
        expirationDate.setMonth(expirationDate.getMonth() + 1);
        this.feature.damageInfo.expiration = expirationDate;
        this.viewerService.updateUserLayer(this.layer);
      }
    }
  }
}
